import React, { useEffect, useState } from 'react';
import { GlobeAltIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { carbonApi, CarbonSummary } from '../../api/carbon';

/**
 * Displays the current month's CO2e footprint sourced from GTS Carbon.
 * Renders nothing visible when the bridge is disabled or unreachable; never
 * blocks the dashboard.
 */
export function CarbonFootprintWidget() {
  const [data, setData] = useState<CarbonSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    carbonApi
      .summary()
      .then((s) => {
        if (!cancelled) setData(s);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message ?? 'Erreur');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openGts = async () => {
    try {
      const { redirectUrl } = await carbonApi.startSso('/dashboard');
      window.location.href = redirectUrl;
    } catch {
      window.alert("Impossible d'ouvrir GTS Carbon — vérifiez votre abonnement.");
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded mb-3" />
        <div className="h-8 w-24 bg-gray-200 rounded" />
      </div>
    );
  }

  if (error || !data || data.enabled === false) {
    return null;
  }

  const total = Number(data.totalKgCo2e ?? 0);

  return (
    <button
      type="button"
      onClick={openGts}
      className="text-left bg-white rounded-lg border border-green-200 p-4 hover:shadow-md hover:border-green-400 transition group cursor-pointer w-full"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
            <GlobeAltIcon className="h-6 w-6 text-green-700" />
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Empreinte carbone
            </div>
            <div className="text-xs text-gray-400">
              {data.period} · GTS Carbon
            </div>
          </div>
        </div>
        <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-300 group-hover:text-green-600" />
      </div>
      <div className="mt-2 text-2xl font-semibold text-green-700">
        {total.toLocaleString('fr-TN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        <span className="text-sm font-normal text-gray-500 ml-1">kg CO₂e</span>
      </div>
      {data.fallback && (
        <div className="text-xs text-amber-600 mt-1">Données indisponibles — réessayer plus tard</div>
      )}
    </button>
  );
}

export default CarbonFootprintWidget;
